import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { get, omit } from "lodash";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import moment from "moment";

// import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import GeneralBuildingInformation from "Components/RegisterBuildingForm/GeneralBuildingInformation";
import UtilityAndMechanicalSystem from "Components/RegisterBuildingForm/UtilityAndMechanicalSystem";
import FireProtection from "Components/RegisterBuildingForm/FireProtection";
import FireLifeSafety from "Components/RegisterBuildingForm/FireLifeSafety";
import FireProtectionEquipment from "Components/RegisterBuildingForm/FireProtectionEquipment";
import Backdrop from "Components/Common/Backdrop";
import Stepper from "common/Stepper";
import { fireDetectionTypeEnum, getOptionValue } from "common/enums";
import { fireSafetyApplicationStatusTypes } from "common/constant";
import { fireSafetyMyRequestRoute } from "Routes/constants";

import { filterEmptyStrings, generateFormData } from "utils";
import { getRegisterBuildingValidation } from "utils/fireSafetyValidationSchema";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useFloorQuery } from "apis/Floor";
import {
  useLazyGetFireApplicationDetailQuery,
  usePostFireApplicationDetailsMutation,
} from "apis/FireSafety/FireApplicationDetail";

const registerBuildingValues = [
  { keyName: "id", defaultValue: 0 },
  { keyName: "authority.id", defaultValue: 0 },
  { keyName: "buildingAuthority.id", defaultValue: 0 },
  { keyName: "responsiblePersonAuthorities", defaultValue: [] },
  { keyName: "fireSafetyAddress.id", defaultValue: 0 },
  { keyName: "fireSafetyCertificate.id", defaultValue: 0 },
  { keyName: "floorWiseTenants", defaultValue: [] },
  { keyName: "utilityMechanicalSystem.id", defaultValue: 0 },
  { keyName: "utilityMechanicalSystem.electricalSystem.id", defaultValue: 0 },
  { keyName: "fireProtection.id", defaultValue: 0 },
  {
    keyName: "fireProtection.fireFightingPumps",
    defaultValue: [],
  },
  {
    keyName: "fireProtection.detectorSystems",
    defaultValue: [],
    isDetectorSystem: true,
  },
  {
    keyName: "fireProtection.basementSprinklers",
    defaultValue: [],
  },
  {
    keyName: "fireProtection.floorSprinklers",
    defaultValue: [],
  },

  { keyName: "lifeSafety.id", defaultValue: 0 },
  {
    keyName: "fireProtection.floorWiseFireExtinguishers",
    defaultValue: [],
    isFloorsKey: true,
  },
  { keyName: "lifeSafety.refugeAreas", defaultValue: [], isFloorsKey: true },
  {
    keyName: "floorWiseFireProtectionEquipments",
    defaultValue: [],
    isFloorsKey: true,
  },
];

const responsiblePersonDetail = {
  id: 0,
  name: "",
  mobileNumber: "",
  email: "",
  designation: "",
  buildingAuthorityRoleId: getOptionValue(
    "buildingAuthorityRoleEnum",
    "Responsible Person"
  ),
};

const RegisterBuilding = ({ t, applicationId, handleClose = () => {} }) => {
  const [
    getApplicationDataById,
    {
      data: { data: applicationData } = {},
      isLoading: isApplicationDataLoading,
    },
  ] = useLazyGetFireApplicationDetailQuery();

  const { data: { data: floors } = {} } = useFloorQuery({}, true);

  const [submitApplication, { isLoading }] =
    usePostFireApplicationDetailsMutation();

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isFinalSubmit, setIsFinalSubmit] = useState(false);

  const initialValues = useMemo(
    () => ({
      id: applicationData?.id ?? 0,
      buildingId: applicationData?.buildingId ?? "",
      fireSafetyOccupancyTypeId: applicationData?.occupancyType?.id ?? "",
      fireSafetyPremiseTypeId: applicationData?.fireSafetyPremiseType?.id ?? "",
      buildUpArea: applicationData?.buildUpArea ?? "",
      numberOfBlock: applicationData?.numberOfBlock ?? "",
      buildingHeight: applicationData?.buildingHeight ?? "",
      mainEntranceWidth: applicationData?.mainEntranceWidth ?? "",
      totalFloorArea: applicationData?.totalFloorArea ?? "",
      frontClearSpace: applicationData?.frontClearSpace ?? "",
      backClearSpace: applicationData?.backClearSpace ?? "",
      rightClearSpace: applicationData?.rightClearSpace ?? "",
      leftClearSpace: applicationData?.leftClearSpace ?? "",
      numberOfFloor: applicationData?.numberOfFloor ?? "",
      buildingNumber: applicationData?.buildingNumber ?? "",
      mentionObstructions: applicationData?.mentionObstructions ?? "",
      fireSafetyCertificate: {
        id: applicationData?.fireSafetyCertificate?.id ?? 0,
        approvalNumber:
          applicationData?.fireSafetyCertificate?.approvalNumber ?? "",
        dateOfIssue: applicationData?.fireSafetyCertificate?.dateOfIssue
          ? convertUTCDateToLocalDate(
              applicationData?.fireSafetyCertificate?.dateOfIssue
            )
          : "",
        dueDate: applicationData?.fireSafetyCertificate?.dueDate
          ? convertUTCDateToLocalDate(
              applicationData?.fireSafetyCertificate?.dueDate
            )
          : "",
      },

      fscrCertificates:
        applicationData?.fscrCertificates.map((item) => ({
          ...item,
          dateOfIssue: item.dateOfIssue
            ? convertUTCDateToLocalDate(item.dateOfIssue)
            : "",
          dueDate: item.dueDate ? convertUTCDateToLocalDate(item.dueDate) : "",
        })) || [],

      fireSafetyAddress: {
        id: applicationData?.fireSafetyAddress?.id ?? 0,
        premisesAreaId:
          applicationData?.fireSafetyAddress?.premiseArea?.id ?? "",
        zoneName: applicationData?.fireSafetyAddress?.zone?.name ?? "",
        blockName: applicationData?.fireSafetyAddress?.block?.name ?? "",
        buildingName: applicationData?.fireSafetyAddress?.building?.name ?? "",
        floors:
          applicationData?.fireSafetyAddress?.floors?.map(({ id, name }) => ({
            label: name,
            value: id,
          })) ?? [],
        nearestLandmark:
          applicationData?.fireSafetyAddress?.nearestLandmark ?? "",
        otherZone: applicationData?.fireSafetyAddress?.otherZone ?? "",
        otherBlock: applicationData?.fireSafetyAddress?.otherBlock ?? "",
        otherBuilding: applicationData?.fireSafetyAddress?.otherBuilding ?? "",
        pinCode: applicationData?.fireSafetyAddress?.pinCode ?? "",
        city: applicationData?.fireSafetyAddress?.city ?? "",
      },

      authority: {
        id: applicationData?.authority?.id ?? 0,
        name: applicationData?.authority?.name ?? "",
        mobileNumber: applicationData?.authority?.mobileNumber ?? "",
        email: applicationData?.authority?.email ?? "",
        buildingAuthorityRoleId:
          applicationData?.authority?.buildingAuthorityRoleId ?? "",
      },

      buildingAuthority: {
        id: applicationData?.buildingAuthority?.id ?? 0,
        name: applicationData?.buildingAuthority?.name ?? "",
        mobileNumber: applicationData?.buildingAuthority?.mobileNumber ?? "",
        email: applicationData?.buildingAuthority?.email ?? "",
        buildingAuthorityRoleId:
          applicationData?.buildingAuthority?.buildingAuthorityRoleId ?? "",
      },

      responsiblePersonAuthorities:
        applicationData?.responsiblePersonAuthorities ?? [
          { ...responsiblePersonDetail },
          { ...responsiblePersonDetail },
          { ...responsiblePersonDetail },
        ],
      floorWiseFireProtectionEquipments:
        applicationData?.floorWiseFireProtectionEquipments?.map((item) => ({
          ...item,
          floors: item.floors.map(({ id, name }) => ({
            label: name,
            value: id,
          })),
        })) ?? [],

      floorWiseTenants: applicationData?.floorWiseTenants ?? [],
      utilityMechanicalSystem: {
        id: applicationData?.utilityMechanicalSystem?.id ?? 0,
        isServiceDuct:
          applicationData?.utilityMechanicalSystem?.isServiceDuct ?? "",
        isServiceDuctsEnclosed:
          applicationData?.utilityMechanicalSystem?.isServiceDuctsEnclosed ??
          "",
        numberOfPassengerLift:
          applicationData?.utilityMechanicalSystem?.numberOfPassengerLift ?? "",
        numberOfFireLift:
          applicationData?.utilityMechanicalSystem?.numberOfFireLift ?? "",
        isAutomaticRescueDeviceForLift:
          applicationData?.utilityMechanicalSystem
            ?.isAutomaticRescueDeviceForLift ?? "",
        numberOfNaturalVentilation:
          applicationData?.utilityMechanicalSystem
            ?.numberOfNaturalVentilation ?? "",
        numberOfMechanicalVentilation:
          applicationData?.utilityMechanicalSystem
            ?.numberOfMechanicalVentilation ?? "",
        otherBuildingSystem:
          applicationData?.utilityMechanicalSystem?.otherBuildingSystem ?? "",
        electricalSystem: {
          id:
            applicationData?.utilityMechanicalSystem?.electricalSystem?.id ?? 0,
          isElectricSubstation:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isElectricSubstation ?? "",
          numberOfTransformers:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.numberOfTransformers ?? "",
          transformerType:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.transformerType ?? "",
          transformerCapacity:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.transformerCapacity ?? "",
          isTransformerForBuildingBlock:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isTransformerForBuildingBlock ?? "",
          transformerLocation:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.transformerLocation ?? "",
          typeOfFireProtectionSystemForTransformer:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.typeOfFireProtectionSystemForTransformer ?? "",
          isSubstation:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isSubstation ?? "",
          isStandbyPower:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isStandbyPower ?? "",
          isStandbyPowerAutomatic:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isStandbyPowerAutomatic ?? "",
          standbyPowerLocation:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.standbyPowerLocation ?? "",
          isUseOfRCCBELCB:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isUseOfRCCBELCB ?? "",
          isEarthing:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.isEarthing ?? "",
          islighteningProtectionSystem:
            applicationData?.utilityMechanicalSystem?.electricalSystem
              ?.islighteningProtectionSystem ?? "",
        },
      },
      fireProtection: {
        id: applicationData?.fireProtection?.id ?? 0,
        underGroundTankCapacity:
          applicationData?.fireProtection?.underGroundTankCapacity ?? "",
        overHeadTankCapacity:
          applicationData?.fireProtection?.overHeadTankCapacity ?? "",
        firePumpLocation:
          applicationData?.fireProtection?.firePumpLocation ?? "",
        isFirstAidHoseReelProvided:
          applicationData?.fireProtection?.isFirstAidHoseReelProvided ?? "",
        numberOfFirstAidHoseReel:
          applicationData?.fireProtection?.numberOfFirstAidHoseReel ?? "",
        isWetControllerSystemProvided:
          applicationData?.fireProtection?.isWetControllerSystemProvided ?? "",
        numberOfWetControllerSystem:
          applicationData?.fireProtection?.numberOfWetControllerSystem ?? "",
        isInternalHydrantProvided:
          applicationData?.fireProtection?.isInternalHydrantProvided ?? "",
        numberOfInternalHydrant:
          applicationData?.fireProtection?.numberOfInternalHydrant ?? "",
        isYardHydrantProvided:
          applicationData?.fireProtection?.isYardHydrantProvided ?? "",
        numberOfYardHydrant:
          applicationData?.fireProtection?.numberOfYardHydrant ?? "",
        isBasementHasSprinklerSystem:
          applicationData?.fireProtection?.isBasementHasSprinklerSystem ?? "",
        basementSprinklers:
          applicationData?.fireProtection?.basementSprinklers ?? [],

        isGroundFloorHasSprinklerSystem:
          applicationData?.fireProtection?.isGroundFloorHasSprinklerSystem ??
          "",
        groundFloorSprinklerSystemQuantity:
          applicationData?.fireProtection?.groundFloorSprinklerSystemQuantity ??
          "",
        isHollowPlinthHasSprinklerSystem:
          applicationData?.fireProtection?.isHollowPlinthHasSprinklerSystem ??
          "",
        hollowPlinthSprinklerSystemQuantity:
          applicationData?.fireProtection
            ?.hollowPlinthSprinklerSystemQuantity ?? "",
        isFloorHasSprinklerSystem:
          applicationData?.fireProtection?.isFloorHasSprinklerSystem ?? "",
        floorSprinklers: applicationData?.fireProtection?.floorSprinklers ?? [],
        detectorSystems:
          applicationData?.fireProtection?.detectorSystems?.map((item) => ({
            ...item,
            detectionTypes: fireDetectionTypeEnum.filter(({ value }) =>
              item.detectionTypes.includes(value)
            ),
          })) ?? [],
        location: applicationData?.fireProtection?.location ?? "",
        isConfiguredInIBMS:
          applicationData?.fireProtection?.isConfiguredInIBMS ?? "",
        isInstalledInTenantArea:
          applicationData?.fireProtection?.isInstalledInTenantArea ?? "",
        isIntegratedWithTenantAccessControl:
          applicationData?.fireProtection
            ?.isIntegratedWithTenantAccessControl ?? "",
        isIntegratedWithMainSystem:
          applicationData?.fireProtection?.isIntegratedWithMainSystem ?? "",
        isIntegratedWithBuildingSystem:
          applicationData?.fireProtection?.isIntegratedWithBuildingSystem ?? "",
        isIntegratedWithCommandCenter:
          applicationData?.fireProtection?.isIntegratedWithCommandCenter ?? "",
        causeAndEffectMatrixDocumentUrl:
          applicationData?.fireProtection?.causeAndEffectMatrixDocumentUrl ??
          "",
        causeAndEffectMatrixDocument:
          applicationData?.fireProtection?.causeAndEffectMatrixDocument ?? "",
        specialFireProtectionSystemProvided:
          applicationData?.fireProtection
            ?.specialFireProtectionSystemProvided ?? "",
        fireFightingPumps:
          applicationData?.fireProtection?.fireFightingPumps ?? [],
        floorWiseFireExtinguishers:
          applicationData?.fireProtection?.floorWiseFireExtinguishers?.map(
            (item) => ({
              ...item,
              floors: item.floors.map(({ id, name }) => ({
                label: name,
                value: id,
              })),
            })
          ) ?? [],
        isEvacuationPlan:
          applicationData?.fireProtection?.isEvacuationPlan ?? "",
        isPublicAddressingSystem:
          applicationData?.fireProtection?.isPublicAddressingSystem ?? false,
        isPublicAddressingSystemInTenantArea:
          applicationData?.fireProtection
            ?.isPublicAddressingSystemInTenantArea ?? "",
        isExternalLifts: applicationData?.fireProtection?.isExternalLifts ?? "",
      },

      lifeSafety: {
        id: applicationData?.lifeSafety?.id ?? 0,
        isCompartmentationDone:
          applicationData?.lifeSafety?.isCompartmentationDone ?? "",
        isLightningArrestorInstalled:
          applicationData?.lifeSafety?.isLightningArrestorInstalled ?? "",
        numberOfInternalStaircases:
          applicationData?.lifeSafety?.numberOfInternalStaircases ?? "",
        internalStaircaseWidth:
          applicationData?.lifeSafety?.internalStaircaseWidth ?? "",
        numberOfExternalStaircases:
          applicationData?.lifeSafety?.numberOfExternalStaircases ?? "",
        isFireExitDoors: applicationData?.lifeSafety?.isFireExitDoors ?? "",
        isBasements: applicationData?.lifeSafety?.isBasements ?? true,
        numberOfBasements: applicationData?.lifeSafety?.numberOfBasements ?? "",
        isBasementHasSprinklers:
          applicationData?.lifeSafety?.isBasementHasSprinklers ?? "",
        basementVentilationType:
          applicationData?.lifeSafety?.basementVentilationType ?? "",
        isAlternatePowerSupply:
          applicationData?.lifeSafety?.isAlternatePowerSupply ?? "",
        isExitSigns: applicationData?.lifeSafety?.isExitSigns ?? "",
        assemblyPointDetail:
          applicationData?.lifeSafety?.assemblyPointDetail ?? "",
        dbrDocuments: applicationData?.lifeSafety?.dbrDocuments ?? [],
        isQualifiedFireOfficer:
          applicationData?.lifeSafety?.isQualifiedFireOfficer ?? "",
        fireOfficerName: applicationData?.lifeSafety?.fireOfficerName ?? "",
        fireOfficerMobileNumber:
          applicationData?.lifeSafety?.fireOfficerMobileNumber ?? "",
        fireOfficerEmailId:
          applicationData?.lifeSafety?.fireOfficerEmailId ?? "",
        refugeAreas:
          applicationData?.lifeSafety?.refugeAreas?.map((item) => ({
            ...item,
            floors: item.floors?.map(({ id, name }) => ({
              label: name,
              value: id,
            })),
          })) ?? [],
      },
    }),
    [applicationData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getRegisterBuildingValidation[currentStep],
    onSubmit: async (values, { setTouched }) => {
      if (currentStep === steps.length - 1 || isFinalSubmit) {
        createRegisterBuildingValues(values);
      } else { 
        setCurrentStep((prev) => prev + 1);
        setTouched({});
      }
      setIsFinalSubmit(false);
    },
  });

  const floorOptions = useMemo(
    () => floors?.map(({ id, name }) => ({ label: name, value: id })) ?? [],
    [floors]
  );

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "General Building Information",
        isCompleted: currentStep >= 1,
        component: (
          <GeneralBuildingInformation
            formik={formik}
            floorOptions={floorOptions}
          />
        ),
      },
      {
        id: 2,
        name: "Utility & Mechanical System",
        isCompleted: currentStep >= 2,
        component: <UtilityAndMechanicalSystem formik={formik} />,
      },
      {
        id: 3,
        name: "Fire Protection",
        isCompleted: currentStep >= 3,
        component: (
          <FireProtection formik={formik} floorOptions={floorOptions} />
        ),
      },
      {
        id: 4,
        name: "Fire Protection Equipment",
        isCompleted: currentStep >= 4,
        component: <FireProtectionEquipment formik={formik} />,
      },
      {
        id: 5,
        name: "Life Safety",
        isCompleted: currentStep >= 5,
        component: <FireLifeSafety formik={formik} />,
      },
    ],
    [currentStep, formik]
  );

  const { handleSubmit, setFieldValue, values } = formik;
  const {
    fireSafetyAddress,
    floorWiseFireProtectionEquipments,
    fireProtection: { floorWiseFireExtinguishers },
    lifeSafety: { refugeAreas },
  } = values;

  const handleDraft = () => {
    createRegisterBuildingValues(values, true);
  };

  const createRegisterBuildingValues = async (values, isDraft = false) => {
    const otherZone = values.fireSafetyAddress.otherZone;
    const otherBlock = values.fireSafetyAddress.otherBlock;
    const otherBuilding = values.fireSafetyAddress.otherBuilding;
    const cityId = values.fireSafetyAddress.city?.id ?? "";

    const applicationDetails = omit(
      {
        ...values,
        fireSafetyAddress: {
          ...values.fireSafetyAddress,
          zoneName:
            values.fireSafetyAddress.zoneName === "other"
              ? otherZone
              : values.fireSafetyAddress.zoneName,
          blockName:
            values.fireSafetyAddress.blockName === "other"
              ? otherBlock
              : values.fireSafetyAddress.blockName,
          buildingName:
            values.fireSafetyAddress.buildingName === "other"
              ? otherBuilding
              : values.fireSafetyAddress.buildingName,
          floors: values.fireSafetyAddress.floors?.map(({ label, value }) => ({
            id: value,
            name: label,
          })),
          cityId,
        },

        fireSafetyCertificate: {
          ...values.fireSafetyCertificate,
          dueDate: values.fireSafetyCertificate.dueDate
            ? values.fireSafetyCertificate.dueDate.format("YYYY-MM-DD")
            : "",
          dateOfIssue: values.fireSafetyCertificate.dateOfIssue
            ? values.fireSafetyCertificate.dateOfIssue.format("YYYY-MM-DD")
            : "",
        },
        fscrCertificates: values.fscrCertificates.map((item) => ({
          ...item,
          dateOfIssue: item.dateOfIssue
            ? moment(item.dateOfIssue).format("YYYY-MM-DD")
            : "",

          dueDate: item.dueDate
            ? moment(item.dueDate).format("YYYY-MM-DD")
            : "",
        })),
        floorWiseFireProtectionEquipments:
          values.floorWiseFireProtectionEquipments.map((item) => ({
            ...item,
            floors: item.floors.map(({ label, value }) => ({
              id: value,
              name: label,
            })),
          })),
        fireProtection: {
          ...values.fireProtection,
          floorWiseFireExtinguishers:
            values.fireProtection.floorWiseFireExtinguishers.map((item) => ({
              ...item,
              floors: item.floors.map(({ label, value }) => ({
                id: value,
                name: label,
              })),
            })),
        },
        lifeSafety: {
          ...values.lifeSafety,
          refugeAreas: values.lifeSafety.refugeAreas.map((item) => ({
            ...item,
            floors: item.floors.map(({ label, value }) => ({
              id: value,
              name: label,
            })),
          })),
          dbrDocuments: values.lifeSafety.dbrDocuments.map(
            ({ file, url, id }) => (file ? { id: 0, file } : { id, url })
          ),
        },
        isDraft,
      },
      [
        "fireSafetyAddress.otherZone",
        "fireSafetyAddress.otherBlock",
        "fireSafetyAddress.otherBuilding",
        "fireSafetyAddress.city",
      ]
    );

    const filteredValues = filterEmptyStrings(applicationDetails);

    const generatePayload = (filteredValues) =>
      Object.entries(filteredValues).reduce((res, [key, value]) => {
        if (Array.isArray(value) && value.length) {
          const filterValue = value
            .map((item) => {
              if (Object.keys(item).length > 1) {
                return generatePayload(item);
              }
            })
            .filter((item) => item);
          if (filterValue.length) {
            return { ...res, [key]: filterValue };
          }
        } else if (typeof value === "object") {
          if (value instanceof File) {
            return { ...res, [key]: value };
          }
          if (Object.keys(value).length > 1) {
            const filterValue = generatePayload(value);
            if (Object.keys(filterValue).length > 1) {
              return { ...res, [key]: filterValue };
            }
          }
        } else {
          return { ...res, [key]: value };
        }
        return res;
      }, {});
    const finalPayload = generatePayload(filteredValues);
    const formData = new FormData();
    generateFormData(formData, {
      ...finalPayload,
      fireProtection: {
        ...finalPayload.fireProtection,
        detectorSystems: finalPayload.fireProtection.detectorSystems?.map(
          (item) => ({
            ...item,
            detectionTypes: item.detectionTypes?.map(({ value }) => value),
          })
        ),
      },
    });

    try {
      const res = await submitApplication(formData);
      if (isDraft) {
        registerBuildingValues.forEach(
          ({
            keyName,
            defaultValue,
            isFloorsKey = false,
            isDetectorSystem = false,
          }) =>
            isFloorsKey
              ? setFieldValue(
                  keyName,
                  get(res.data, keyName, defaultValue).map((item) => ({
                    ...item,
                    floors: item.floors.map(({ id, name }) => ({
                      label: name,
                      value: id,
                    })),
                  }))
                )
              : isDetectorSystem
              ? setFieldValue(
                  keyName,
                  get(res.data, keyName, defaultValue).map((item) => ({
                    ...item,
                    detectionTypes: fireDetectionTypeEnum.filter(({ value }) =>
                      item.detectionTypes.includes(value)
                    ),
                  }))
                )
              : setFieldValue(keyName, get(res.data, keyName, defaultValue))
        );
      } else {
        handleClose();
        if (!applicationData) {
          navigate(fireSafetyMyRequestRoute);
        }
      }
    } catch (error) {}
  };

  // reset value which is dependent on floors if floors are not selected
  const filterDataDependentOnFloors = (keyName, floors, data) => {
    if (!floors.length && data.length) {
      setFieldValue(keyName, []);
    }
    if (floors.length && data.length) {
      const floorIds = floors.map(({ value }) => value);
      const updatedDetails = data.map((item) => ({
        ...item,
        floors: item.floors.filter(({ value }) => floorIds.includes(value)),
      }));
      setFieldValue(
        keyName,
        updatedDetails.filter(({ floors }) => !!floors.length)
      );
    }
  };

  useEffect(() => {
    filterDataDependentOnFloors(
      "fireProtection.floorWiseFireExtinguishers",
      fireSafetyAddress.floors,
      floorWiseFireExtinguishers
    );
    filterDataDependentOnFloors(
      "floorWiseFireProtectionEquipments",
      fireSafetyAddress.floors,
      floorWiseFireProtectionEquipments
    );
    filterDataDependentOnFloors(
      "lifeSafety.refugeAreas",
      fireSafetyAddress.floors,
      refugeAreas
    );
  }, [fireSafetyAddress.floors]);

  // get registerBuilding data on edit
  useEffect(() => {
    (async () => {
      if (applicationId) {
        try {
          await getApplicationDataById(applicationId);
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, [applicationId]);

  return (
    <div className="h-100">
      <Backdrop open={isApplicationDataLoading} />

      {!applicationId && (
        <BreadCrumb title={t("Register Building")} pageTitle={t("")} />
      )}

      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          handleDraft={handleDraft}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          isLoading={isLoading}
          formik={formik}
          applicationId={applicationId}
          setIsFinalSubmit={setIsFinalSubmit}
          isDraftDisabled={
            !!applicationId &&
            applicationData?.buildingInformationStatus?.id !==
              fireSafetyApplicationStatusTypes["Saved"]
          }
        />
      </div>
    </div>
  );
};

RegisterBuilding.propTypes = {
  t: PropTypes.func,
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
};

export default withTranslation()(RegisterBuilding);
